import { Capacitor, registerPlugin } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";

const nativeFiles = registerPlugin("ForktoFiles");
const CACHE_ROOT = "forkto-exports";
const RETENTION_MS = 24 * 60 * 60 * 1000;
const CHUNK_BYTES = 512 * 1024;
let exporting = false;

const mimeTypes = {
    csv: "text/csv",
    pdf: "application/pdf",
    zip: "application/zip",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    txt: "text/plain",
    json: "application/json",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
};

export function safeExportFilename(name) {
    const cleaned =
        String(name || "file")
            .normalize("NFC")
            .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]/g, "-")
            .replace(/^[.\s]+|[.\s]+$/g, "") || "file";
    const encoder = new TextEncoder();
    if (encoder.encode(cleaned).length <= 160) return cleaned;
    const dot = cleaned.lastIndexOf(".");
    const extension = dot > 0 && cleaned.length - dot <= 12 ? cleaned.slice(dot) : "";
    const stem = extension ? cleaned.slice(0, dot) : cleaned;
    let truncated = "";
    let bytes = encoder.encode(extension).length;
    for (const character of stem) {
        bytes += encoder.encode(character).length;
        if (bytes > 160) break;
        truncated += character;
    }
    return truncated + extension;
}

function normalizeFiles(files) {
    if (!files?.length) throw new Error("Choose at least one file to export.");
    const names = new Set();
    return files.map(({ blob, filename }) => {
        if (!(blob instanceof Blob)) throw new Error("The file could not be read.");
        const base = safeExportFilename(filename);
        const dot = base.lastIndexOf(".");
        const stem = dot > 0 ? base.slice(0, dot) : base;
        const extension = dot > 0 ? base.slice(dot) : "";
        let name = base;
        for (let suffix = 2; names.has(name.toLowerCase()); suffix++)
            name = `${stem} (${suffix})${extension}`;
        names.add(name.toLowerCase());
        return {
            blob,
            filename: name,
            mimeType:
                blob.type && blob.type !== "application/octet-stream"
                    ? blob.type.split(";")[0]
                    : mimeTypes[extension.slice(1).toLowerCase()] || "application/octet-stream"
        };
    });
}

function base64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(new Error("The file could not be prepared for export."));
        reader.readAsDataURL(blob);
    });
}

async function removeBatch(path) {
    try {
        await Filesystem.rmdir({ path, directory: Directory.Cache, recursive: true });
    } catch {
        /* Cleanup must not replace the export outcome or original failure. */
    }
}

async function pruneExports() {
    // Keep shared files long enough for receiving applications to finish reading.
    // Cache is disposable; a missing directory or cleanup failure must not block an export.
    try {
        const { files } = await Filesystem.readdir({
            path: CACHE_ROOT,
            directory: Directory.Cache
        });
        for (const file of files) {
            const timestamp = Number(file.name.split("-")[0]);
            if (timestamp > 0 && timestamp < Date.now() - RETENTION_MS)
                await removeBatch(`${CACHE_ROOT}/${file.name}`);
        }
    } catch {
        /* First export or cache evicted by the OS. */
    }
}

async function stageFile(file, batch) {
    const path = `${batch}/${file.filename}`;
    // Decode and append bounded chunks natively instead of bridging one large base64 string.
    for (let offset = 0; offset < file.blob.size || offset === 0; offset += CHUNK_BYTES) {
        const data = await base64(file.blob.slice(offset, offset + CHUNK_BYTES));
        const options = { path, directory: Directory.Cache, data };
        if (offset === 0) await Filesystem.writeFile({ ...options, recursive: true });
        else await Filesystem.appendFile(options);
    }
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    return { uri, filename: file.filename, mimeType: file.mimeType };
}

function browserDownload({ blob, filename, mimeType }) {
    const url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: mimeType }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    try {
        link.click();
    } finally {
        link.remove();
        // Allow browsers to consume the object URL before releasing it.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
}

export async function exportFiles(input) {
    if (exporting) throw new Error("Finish the current file export before starting another.");
    exporting = true;
    let batch;
    try {
        const files = normalizeFiles(input);
        if (!Capacitor.isNativePlatform()) {
            files.forEach(browserDownload);
            return { mode: "downloaded", count: files.length };
        }
        if (!Capacitor.isPluginAvailable("ForktoFiles")) {
            throw new Error("Update the Forkto app to save or share files on this device.");
        }
        await pruneExports();
        batch = `${CACHE_ROOT}/${Date.now()}-${crypto.randomUUID()}`;
        const prepared = [];
        for (const file of files) prepared.push(await stageFile(file, batch));
        const result = await nativeFiles.exportFiles({ files: prepared });
        if (!["saved", "shared", "presented", "cancelled"].includes(result?.mode)) {
            throw new Error("The file export could not be confirmed.");
        }
        if (result.mode === "cancelled" || result.mode === "saved") await removeBatch(batch);
        return { ...result, count: result.mode === "cancelled" ? 0 : files.length };
    } catch (error) {
        if (batch) await removeBatch(batch);
        throw error;
    } finally {
        exporting = false;
    }
}

export function exportFile(blob, filename) {
    return exportFiles([{ blob, filename }]);
}

// Large archives are downloaded by the OS straight into a private cache file.
// Browser downloads use the signed URL and the storage response's filename.
// Neither path materializes the archive in JavaScript memory.
export async function exportDownload({ url, filename, size, signal, canContinue = () => true }) {
    if (exporting) throw new Error("Finish the current file export before starting another.");
    if (new URL(url).protocol !== "https:") throw new Error("The download address is invalid.");
    const name = safeExportFilename(filename);
    exporting = true;
    let batch;
    try {
        if (!Capacitor.isNativePlatform()) {
            const link = document.createElement("a");
            link.href = url;
            link.download = name;
            link.rel = "noopener";
            document.body.appendChild(link);
            try {
                link.click();
            } finally {
                link.remove();
            }
            return { mode: "downloaded", count: 1 };
        }
        if (!Capacitor.isPluginAvailable("ForktoFiles"))
            throw new Error("Update the Forkto app to save or share files on this device.");
        if (!Number.isSafeInteger(size) || size <= 0)
            throw new Error("The download size is invalid.");
        await pruneExports();
        batch = `${CACHE_ROOT}/${Date.now()}-${crypto.randomUUID()}`;
        const { uri } = await nativeFiles.downloadFile({ url, filename: name, size, batch });
        if (signal?.aborted || !canContinue())
            throw new DOMException("Export paused", "AbortError");
        const result = await nativeFiles.exportFiles({
            files: [{ uri, filename: name, mimeType: "application/zip" }]
        });
        if (!["saved", "shared", "presented", "cancelled"].includes(result?.mode))
            throw new Error("The file export could not be confirmed.");
        if (result.mode === "cancelled" || result.mode === "saved") await removeBatch(batch);
        return { ...result, count: result.mode === "cancelled" ? 0 : 1 };
    } catch (error) {
        if (batch) await removeBatch(batch);
        if (error?.code === "UNIMPLEMENTED")
            throw new Error("Update the Forkto app to download large data exports on this device.");
        throw error;
    } finally {
        exporting = false;
    }
}

export function fileExportMessage(result, label = "File") {
    if (result?.mode === "saved")
        return `${label} saved${result.format === "zip" ? " as a ZIP" : ""} to your chosen location`;
    if (result?.mode === "shared") return `${label} handed to your selected destination`;
    if (result?.mode === "downloaded") return `${label} download started`;
    // Android chooser completion cannot prove that the receiving app saved a file.
    return "";
}

export function notifyFileExport(result, toast, label = "File") {
    const message = fileExportMessage(result, label);
    if (message) toast?.success(message);
}
